import timezonesJSON from '@assets/timezones.json'
import { MapboxAwaiter } from '@components/Maps/Mapbox/mapbox_awaiter'
import { getRemainingData, getResultData } from '@components/Maps/pointsHelpers'
import { asArray } from '@shared_utils/array_utils'
import type { Feature } from '@turf/helpers/dist/js/lib/geojson'
import { along, length } from '@turf/turf'
import { toLatLng } from '@utils/geo_utils'
import type { AnySourceData, LineLayer, LngLatLike, Map } from 'mapbox-gl'

import { getPositionOfPointOnRoute } from '../mapOptions'
import type { Result } from '../WindyWrapper'
import { removeLayerFrom } from './MapboxLayers'

function createSourceFromCoordinates (coordinates: LngLatLike[]): AnySourceData {
  return {
    type: 'geojson',
    data: {
      type: 'Feature',
      properties: {},
      geometry: {
        type: 'LineString',
        coordinates: coordinates.map(c => {
          const { lng, lat } = toLatLng(c)
          return [lng, lat]
        })
      }
    }
  }
}

interface TransformRouteProps {
  points: LngLatLike[]
  shipPosition: number[]
  type: 'route' | 'itinerary'
}

function transformRoute (p: TransformRouteProps) {
  const { shipPosition, type } = p

  if (type !== 'route' && type !== 'itinerary') {
    return []
  }

  const newRoute = [...p.points]
  const transformedCoords: number[][] = p.points.map(item => {
    const ll = toLatLng(item)
    return [ll.lng, ll.lat]
  })

  const currentPositionOnRoute: Result | undefined | 0 = getPositionOfPointOnRoute(shipPosition, transformedCoords)

  if (currentPositionOnRoute) {
    const shipPositionLatLng = {
      lat: shipPosition[1],
      lng: shipPosition[0]
    }

    const { position } = currentPositionOnRoute
    newRoute.splice(position, 1, shipPositionLatLng)
  }

  if (type === 'route') {
    return getRemainingData(currentPositionOnRoute, transformedCoords, shipPosition).remainingRouteData
  }

  return getResultData(currentPositionOnRoute, transformedCoords, shipPosition).routeData
}

function roundedPolyline (id: string, points: LngLatLike[], lineColor: string, lineWidth = 2, lineDash?: number[]) {
  return new MapboxPolyline(
    id,
    points,
    {
      'line-join': 'round',
      'line-cap': 'round'
    },
    {
      'line-color': lineColor,
      'line-width': lineWidth,
      ...(lineDash && { 'line-dasharray': lineDash })
    }
  )
}

export default class MapboxPolyline {
  private readonly _layer: LineLayer
  private readonly _points: LngLatLike[]

  constructor (id: string, points: LngLatLike[], layout: object, paint: object) {
    this._points = points
    this._layer = { id, source: id, type: 'line', layout, paint }
  }

  get id () {
    return this._layer.id
  }

  get layer () {
    return this._layer
  }

  get center () {
    let lat = 0
    let lng = 0
    if (this._points.length > 1) {
      const beginning = toLatLng(this._points[0])
      const end = toLatLng(this._points[this._points.length - 1])
      lat = (beginning.lat + end.lat) / 2
      lng = (beginning.lng + end.lng) / 2
    }
    return { lat, lng }
  }

  get source (): AnySourceData {
    if (this.id === 'timezones' || this.id === 'arc') {
      return { type: 'geojson', data: this._points as unknown as string }
    } else {
      return createSourceFromCoordinates(this._points)
    }
  }

  static updateMap (map: Map, oldIds: string[], lines?: Array<MapboxPolyline | null>) {
    if (!map) {
      console.trace('updateMap not possible, no map instance given')
      return []
    }

    const validLines = asArray(lines).filter(l => !!l) as MapboxPolyline[]
    const newIds: string[] = validLines.map(l => l.id)

    for (const oldId of oldIds) {
      if (!newIds.includes(oldId)) {
        removeLayerFrom(map, oldId)
      }
    }

    if (!map) {
      return newIds
    }

    setTimeout(async () => {
      const awaiter = new MapboxAwaiter()
      for (const polyline of validLines) {
        await awaiter.addLayer(map, polyline.layer, polyline.source)
      }
    }, 0)

    return newIds
  }

  static route (points: LngLatLike[], shipPosition: number[]) {
    if (points.length < 2 || shipPosition.length < 2) {
      return null
    }

    const routePoints = transformRoute({
      points,
      shipPosition,
      type: 'route'
    }) as LngLatLike[]

    return roundedPolyline('route', routePoints, '#fff', 2, [1, 2])
  }

  static createItinerary (points: LngLatLike[], shipPosition: number[]) {
    if (points.length < 2) {
      return null
    }

    const routePoints = transformRoute({
      points,
      shipPosition,
      type: 'itinerary'
    }) as LngLatLike[]
    return roundedPolyline('itinerary', routePoints, '#fff')
  }

  static timezones () {
    return roundedPolyline('timezones', timezonesJSON as unknown as LngLatLike[], 'grey', 1)
  }

  static arc (from: LngLatLike, to: LngLatLike, steps: number) {
    if (!from || !to) {
      return null
    }

    const _from = toLatLng(from)
    const _to = toLatLng(to)

    const source = {
      type: 'FeatureCollection',
      features: [
        {
          type: 'Feature',
          geometry: {
            type: 'LineString',
            coordinates: [
              [_from.lng, _from.lat],
              [_to.lng, _to.lat]
            ]
          }
        }
      ]
    }

    const dist = length(source.features[0] as unknown as Feature)
    const arc = []

    for (let i = 0; i < dist; i += dist / steps) {
      // @ts-expect-error mapbox types error
      const segment = along(source.features[0], i)
      arc.push(segment.geometry.coordinates)
    }

    source.features[0].geometry.coordinates = arc

    return new MapboxPolyline(
      'arc',
      // @ts-expect-error mapbox types error
      source,
      {},
      {
        'line-width': 5,
        'line-color': '#33C1FA'
      }
    )
  }
}
